import { Button, Link } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useId, useRef } from 'react'
import { useTheoryLevel } from '../theory-copy/use-theory-copy.js'
import { chordCheckCopy, chordCheckWhy, type ChordCheckHintLike } from './chord-check-copy.js'
import styles from './chord-check-section.module.css'

export interface ChordCheckPlace {
  /** Section label, e.g. "Chorus 2". */
  label: string
  onGo: () => void
}

export interface ChordCheckSectionProps {
  hint: ChordCheckHintLike
  /** The chord as the chip shows it, e.g. "E". */
  chordName: string
  /** Other places the same flagged chord appears (Joni §4). Empty when it appears once. */
  otherPlaces?: ChordCheckPlace[]
  /** Omit where the host already has a play control, e.g. the chord popup's header (Joni, #2426 P3). */
  onHear?: () => void
  onKeep: () => void
  /** Move focus to the heading on mount, for the Review path (Joni §5). */
  autoFocus?: boolean
  /**
   * During a review, the heading also names the chord for screen readers ("…: E"), since focus lands on
   * it as the popup moves from chord to chord (Joni, #2438 P2).
   */
  reviewing?: boolean
  /** During a review, the step to the next distinct marked chord (Joni §4). */
  next?: { position: number; total: number; onNext: () => void }
}

/**
 * "Worth a second listen": the marker's explanation and actions at the top of the chord popup and the
 * chord properties panel (Joni §3–§5). An annotation, not an error — no warning colour or icon.
 */
export const ChordCheckSection: FC<ChordCheckSectionProps> = props => {
  const { hint, chordName, otherPlaces = [], onHear, onKeep, autoFocus = false, reviewing = false, next } = props
  const level = useTheoryLevel()
  const headingRef = useRef<HTMLHeadingElement>(null)
  // The popup and the properties panel can both be mounted: each section labels itself by its own heading
  const headingId = useId()

  useEffect(() => {
    if (autoFocus) {
      headingRef.current?.focus()
    }
  }, [autoFocus])

  return (
    <section className={styles.section} aria-labelledby={headingId} data-chord-check-section>
      <h3 id={headingId} ref={headingRef} tabIndex={-1} className={styles.heading}>
        {chordCheckCopy.heading}
        {reviewing ? <span className={styles.visuallyHidden}>{`: ${chordName}`}</span> : null}
      </h3>
      <p className={styles.why}>{chordCheckWhy(hint, chordName, level)}</p>
      <p className={styles.listen}>{chordCheckCopy.listen}</p>
      {otherPlaces.length > 0 ? (
        <p className={styles.alsoIn}>
          {'Also in '}
          {otherPlaces.map((place, i) => (
            <span key={`${place.label}-${i}`}>
              {i > 0 ? (i === otherPlaces.length - 1 ? ' and ' : ', ') : null}
              <Link component="button" type="button" onClick={place.onGo} className={styles.placeLink}>
                {place.label}
              </Link>
            </span>
          ))}
        </p>
      ) : null}
      <div className={styles.actions}>
        {onHear ? (
          <Button size="small" variant="text" onClick={onHear}>
            {chordCheckCopy.hear}
          </Button>
        ) : null}
        <Button size="small" variant="outlined" onClick={onKeep}>
          {chordCheckCopy.keep}
        </Button>
        {next ? (
          <Button size="small" variant="text" onClick={next.onNext}>
            {chordCheckCopy.next(next.position, next.total)}
          </Button>
        ) : null}
      </div>
    </section>
  )
}
